import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { createContext, useContext, useEffect, type PropsWithChildren } from "react";

import {
  fetchAdminSession,
  type AdminSession,
} from "./admin-session";
import { ADMIN_REAUTH_REQUIRED, adminErrorMessage, isAdminApiError } from "../api/admin-errors";
import { adminQueryKeys } from "../api/query-keys";

interface AdminContextValue {
  readonly session: AdminSession;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function useAdminSession(): AdminSession {
  const value = useContext(AdminContext);
  if (!value) {
    throw new Error("useAdminSession must be used inside AdminGuard");
  }
  return value.session;
}

function AdminLoading() {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--color-surface-muted)] p-6">
      <p role="status">Carregando o painel administrativo…</p>
    </main>
  );
}

function AdminForbidden() {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--color-surface-muted)] p-6">
      <section aria-labelledby="admin-forbidden-title" className="grid max-w-lg gap-3 rounded-[var(--radius-surface)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-floating)]">
        <h1 className="m-0 text-2xl font-bold" id="admin-forbidden-title">Acesso administrativo não autorizado</h1>
        <p className="m-0 text-[var(--color-muted)]">Sua conta não possui uma permissão ativa para este painel.</p>
        <a className="font-bold text-[var(--color-action)] underline" href="/minha-conta">Voltar para minha conta</a>
      </section>
    </main>
  );
}

function AdminUnavailable({
  error,
  isRetrying,
  onRetry,
}: {
  readonly error: unknown;
  readonly isRetrying: boolean;
  readonly onRetry: () => void;
}) {
  const correlationId = isAdminApiError(error) ? error.correlationId : null;

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--color-surface-muted)] p-6">
      <section aria-labelledby="admin-unavailable-title" className="grid max-w-lg gap-3 rounded-[var(--radius-surface)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-floating)]">
        <h1 className="m-0 text-2xl font-bold" id="admin-unavailable-title">Painel administrativo indisponível</h1>
        <p className="m-0 text-[var(--color-muted)]" role="alert">{adminErrorMessage(error, "Não foi possível carregar o painel administrativo.")}</p>
        {correlationId ? <p className="m-0 text-[var(--color-muted)]">Correlação: <code>{correlationId}</code></p> : null}
        <button className="font-bold text-[var(--color-action)] underline" disabled={isRetrying} onClick={onRetry} type="button">
          {isRetrying ? "Tentando novamente…" : "Tentar novamente"}
        </button>
      </section>
    </main>
  );
}

function adminSessionLost(error: unknown): boolean {
  return (
    isAdminApiError(error) && error.status === 401 && error.code !== ADMIN_REAUTH_REQUIRED
  );
}

export function AdminGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnTo = `${location.pathname}${location.search}`;
  const query = useQuery({
    queryKey: adminQueryKeys.session,
    queryFn: ({ signal }) => fetchAdminSession(signal),
    retry: false,
  });

  useEffect(() => {
    const redirectToLogin = (error: unknown) => {
      if (!adminSessionLost(error)) {
        return;
      }
      void navigate("/entrar", {
        replace: true,
        state: { from: returnTo, sessionExpired: true },
      });
    };
    const unsubscribers = [
      queryClient.getQueryCache().subscribe((event) => {
        if (event.query.state.status === "error") {
          redirectToLogin(event.query.state.error);
        }
      }),
      queryClient.getMutationCache().subscribe((event) => {
        if (event.mutation?.state.status === "error") {
          redirectToLogin(event.mutation.state.error);
        }
      }),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [navigate, queryClient, returnTo]);

  if (query.isPending) {
    return <AdminLoading />;
  }
  if (query.isError) {
    if (isAdminApiError(query.error) && query.error.status === 401) {
      return (
        <Navigate
          replace
          state={{ from: returnTo, sessionExpired: query.error.code !== ADMIN_REAUTH_REQUIRED }}
          to="/entrar"
        />
      );
    }
    if (isAdminApiError(query.error) && query.error.status === 403) {
      return <AdminForbidden />;
    }
    return <AdminUnavailable error={query.error} isRetrying={query.isFetching} onRetry={() => void query.refetch()} />;
  }

  return (
    <AdminContext.Provider value={{ session: query.data }}>
      {children ?? <Outlet />}
    </AdminContext.Provider>
  );
}
